import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ENTER } from '@angular/cdk/keycodes';
import { Component, computed, output, signal } from '@angular/core';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';

import { GenericObjectType } from '@models/helpers';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { CipoControlBase } from '@shared/components/fields/cipo-control.base';
import { CipoFormModule } from '@shared/modules';
import { CipoTextViewComponent } from '../cipo-text-view/cipo-text-view.component';
import { CipoListConfig } from '../common';

@Component({
  selector: 'cipo-autocomplete',
  imports: [CdkDropList, CipoFormModule, MatAutocompleteModule, CipoTextViewComponent, CdkDrag, FlexLayoutModule],
  templateUrl: './cipo-autocomplete.component.html',
})
export class CipoAutocompleteComponent extends CipoControlBase<string[], CipoListConfig> {
  addNewValue = output<string>();
  suffixClicked = output<void>();

  readonly separatorKeysCodes = [ENTER];
  searchCriteria = signal('');
  private optionSelected = false;

  optionsDict = computed(() => {
    const options = this.fieldData()?.options;
    if (!options) {
      return {};
    }
    return options.reduce((acc, option) => {
      acc[option.key] = option.value;
      return acc;
    }, {} as GenericObjectType);
  });

  availableOptions = computed(() => {
    const search = this.searchCriteria().toLowerCase();
    const selectedValues = this.formValue() || [];
    return this.deepFieldData
      .options()
      ?.filter(
        ({ key, value }) =>
          (value.toLowerCase().includes(search) || (typeof key === 'string' && key.toLowerCase().includes(search))) &&
          !selectedValues.includes(key),
      );
  });

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) return;

    setTimeout(() => {
      // We need this because pressing enter on an autocomplete option will trigger the add event
      if (this.optionSelected) {
        this.optionSelected = false;
        return;
      }
      const control = this.control();
      const controlValue: string[] = control.value || [];
      if (controlValue.includes(value)) return;

      if (this.fieldData().allowNewValues) {
        control.patchValue([...controlValue, value]);
        this.addNewValue.emit(value);
      } else {
        const potentialOption = this.availableOptions().find(
          option => option.value.toLowerCase() === value.toLowerCase(),
        );
        if (!potentialOption || potentialOption.isDisabled) return;
        control.patchValue([...controlValue, potentialOption.key]);
      }
      event.chipInput!.clear();
      this.searchCriteria.set('');
    }, 10);
  }

  remove(option: string): void {
    if (!this.isRemovable(option)) {
      return;
    }
    const currentValue = this.control().value || [];
    this.control().setValue(currentValue.filter(item => item !== option));
  }

  isRemovable(option: string | number): boolean {
    return !this.getOptionByKey(option)?.isDisabled;
  }

  private getOptionByKey(optionKey: string | number) {
    const normalizedKey = String(optionKey);
    return this.deepFieldData.options()?.find(option => String(option.key) === normalizedKey);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const option = this.deepFieldData.options()?.find(item => item.key === event.option.value);
    if (option?.isDisabled) {
      return;
    }
    this.optionSelected = true;
    const currentValue = this.control().value || [];
    this.control().patchValue([...currentValue, event.option.value]);
    this.searchCriteria.set('');
  }

  drop(event: CdkDragDrop<string[]>) {
    const val = [...(this.control().value ?? [])];
    if (!val.length) {
      return;
    }
    moveItemInArray(val, event.previousIndex, event.currentIndex);
    this.control().setValue(val);
  }

  suffixClickedHandler(event: MouseEvent) {
    event.stopPropagation();
    this.suffixClicked.emit();
  }
}
